import {
	Component,
	Input,
	AfterViewInit,
	ViewChild,
	OnInit,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	NgModel,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import {
	SelectedUserEvent,
	UserType,
} from 'src/app/components/user-search-bar/user-search-bar.component';
import { Country } from 'src/app/models/country';
import { emptyAddress } from 'src/app/models/data/address';
import { ApiService } from 'src/app/services/api.services';
import { GeoService } from 'src/app/services/geo.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { EditCompany } from '../add-company-main-tab.component';
import { Timezone } from 'src/app/models/timezone';
import Company = Components.Schemas.Company;
import User = Components.Schemas.User;
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import {
	MatSlideToggleChange,
	MatSlideToggle,
} from '@angular/material/slide-toggle';
import { UserSessionService } from 'src/app/services/user-session.service';
import { NotAuthorizedNoticeComponent } from '../../../components/not-authorized-notice/not-authorized-notice.component';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatHint,
	MatError,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass, AsyncPipe } from '@angular/common';
import { UserSearchBarComponent } from '../../../components/user-search-bar/user-search-bar.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';

@Component({
	selector: 'app-add-company-about',
	templateUrl: './add-company-about.component.html',
	styleUrls: [
		'./add-company-about.component.scss',
		'../../common/main.scss',
		'../../common/stepper.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		HasSystemCapabilityDirective,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatOption,
		MatHint,
		MatSelect,
		MatError,
		MatChipSet,
		MatChip,
		NgClass,
		UserSearchBarComponent,
		MatRadioGroup,
		MatRadioButton,
		MatSlideToggle,
		ReactiveFormsModule,
		MatIcon,
		MatSuffix,
		AsyncPipe,
	],
})
export class AddCompanyAboutComponent implements AfterViewInit, OnInit {
	@Input() editCompany!: EditCompany;
	@Input() companyType!: CompanyType;

	@ViewChild('taxIdModel') taxIdModel!: NgModel;

	filteredCountries!: Observable<Country[]>;

	filteredTimezones!: Observable<Timezone[]>;
	timezones!: Timezone[];

	allCountries: Country[] = [];

	addUserDescriptionText = $localize`:@@addCompanyAbout-addUserDescriptionTextNonOracle:Add a User that will be the Company Administrator for the created company.`;
	addUserHeaderText = $localize`:@@addCompanyAbout-addUserHeaderText:Add/Select Company Administrator`;

	statusActive = $localize`:@@addCompanyAbout-statusActive:Active`;
	statusInactive = $localize`:@@addCompanyAbout-statusInactive:Inactive`;
	foundDuplicate = false;
	isOracle = false;
	showCountryError = false;
	showTimezoneError = false;
	isSentriKeyAccessUser = false;
	hidePwd = true;
	ajaxFormGroup!: FormGroup;
	taxIdValidated = false;
	foundTaxIdDuplicate = false;

	constructor(
		private readonly router: Router,
		private readonly apiService: ApiService,
		public readonly geoService: GeoService,
		private readonly navHistory: NavigationHistoryService,
		private userSessionService: UserSessionService,
		private readonly scService: SystemCapabilityService,
		private fb: FormBuilder
	) {
		this.allCountries = this.geoService.allCountries;
		this.isOracle = this.scService.isOracle;

		if (this.isOracle) {
			this.addUserDescriptionText = $localize`:@@addCompanyAbout-addUserDescriptionTextOracle:Add or Select a User that will be the Company Administrator for the created company.`;
		}

		this.ajaxFormGroup = this.fb.group({
			ajaxUsername: ['', []],
			ajaxPassword: ['', []],
		});
		this.setupDynamicAjaxValidators();
	}
	readonly activeOptions = [
		{ value: true, label: $localize`:@@addCompanyAbout-Active:Active` },
		{ value: false, label: $localize`:@@addCompanyAbout-Inactive:Inactive` },
	];

	ngOnInit(): void {
		// For new company, I need to set a toggle true for requireReasonForCodeGeneration which is set from below code :
		if (!this.isExistingCompany) {
			this.editCompany.company = {
				...this.editCompany?.company,
				requireReasonForCodeGeneration: true,
			};
		}
	}

	async ngAfterViewInit(): Promise<void> {
		const sentrilockCompanyId = await this.apiService.getSentrilockCompanyId();
		const currentCompanyId =
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId;
		this.isSentriKeyAccessUser = currentCompanyId === sentrilockCompanyId;

		this.timezones = await this.apiService.getTimezone();
		if (!this.isOracle) {
			// set the default to 48 months and the company admin/other role will need to contact keynetics to change this period.
			this.editCompany.company.deidentification = 48;
		}
	}

	filterTimezones(value: any): void {
		this.filteredTimezones = of(this._filterTimezone(value));
	}

	private _filterTimezone(value: string): Timezone[] {
		const filterValue = value.toLowerCase();
		return this.timezones.filter(
			(res) =>
				res.timezone.toLowerCase().includes(filterValue) ||
				res.description?.toLowerCase().includes(filterValue)
		);
	}

	openAutocompleteCountries() {
		this.filteredCountries = of(this.allCountries);
	}

	openAutocompleteTimezones() {
		this.filteredTimezones = of(this.timezones);
	}

	countrySelected() {
		this.showCountryError = false;
	}

	countryInput() {
		this.showCountryError = true;
	}

	timezoneSelected() {
		// show the error if user does not select a country from the list per the requirements
		this.showTimezoneError = false;
	}

	timezoneInput() {
		// show the error if user does not select a timezone from the list per the requirements
		this.showTimezoneError = true;
	}

	displayTimezoneName = (tz: string): string => {
		let foundTimezone = null;
		if (this.timezones) {
			foundTimezone = this.timezones.find((res) => res.timezone === tz);
		}

		if (foundTimezone?.timezone) {
			return foundTimezone.timezone + ' - ' + foundTimezone.description;
		} else {
			return foundTimezone?.timezone ?? '';
		}
	};

	get isExistingCompany(): boolean {
		return this.companyType === CompanyType.existingCompany;
	}

	public isValid = (): boolean => {
		switch (this.companyType) {
			case CompanyType.newCompany:
				const validCountryCode = this.validateCountryCode(this.countryCode);

				if (!validCountryCode) {
					return false;
				}

				const requiredCompanyFields = [
					this.name,
					this.countryCode,
					this.taxId,
					this.timezone,
				];

				const companyComplete = requiredCompanyFields.reduce(
					(accumulator, value) => value.length > 0 && accumulator,
					true
				);

				let requiredUserFields = [this.editCompany.adminUser.user.email];
				if (this.editCompany.adminUser.userType === UserType.newUser) {
					requiredUserFields = requiredUserFields.concat([
						this.editCompany.adminUser.user.firstName,
						this.editCompany.adminUser.user.lastName,
					]);
				}
				const userComplete = requiredUserFields.reduce(
					(accumulator, value) => value.length > 0 && accumulator,
					true
				);

				const relationshipTypeSelected =
					this.editCompany.relationshipType.length > 0;

				const deidentificationSelected =
					!!this.editCompany.company.deidentification &&
					this.editCompany.company.deidentification > 0 &&
					this.editCompany.company.deidentification < 49;

				const apiUserEntered = this.editCompany.company.apiEnabled
					? (this.editCompany.editApiUsernames?.length ?? 0) > 0
					: true;

				if (this.ajaxFormGroup) {
					const ajaxFormValues = this.ajaxFormGroup.value;
					this.editCompany.company.ajaxUsername = ajaxFormValues.ajaxUsername;
					this.editCompany.company.ajaxPassword = ajaxFormValues.ajaxPassword;
				}

				const ajaxUserEntered =
					this.editCompany.company.ajaxEnabled && this.isSentriKeyAccessUser
						? this.ajaxFormGroup.valid
						: true;

				return (
					companyComplete &&
					userComplete &&
					relationshipTypeSelected &&
					deidentificationSelected &&
					apiUserEntered &&
					ajaxUserEntered &&
					this.taxIdValidated &&
					!this.foundTaxIdDuplicate
				);
			case CompanyType.existingCompany:
				return (
					this.editCompany.relationshipType.length > 0 &&
					!!this.editCompany.company.deidentification
				);
			default:
				return true;
		}
	};

	isInvalidString(str?: string): boolean {
		return typeof str !== 'string' || str!.trim() === '';
	}

	public doesCompanyAlreadyExist = async (): Promise<boolean> => {
		this.foundDuplicate = false;
		const queryParams = {
			name: this.editCompany.company.name,
			taxId: this.editCompany.company.taxId ?? '',
			countryCode: this.editCompany.company.address?.countryCode ?? '',
		};
		const existingCompanies =
			await this.apiService.getCompanyByInfo(queryParams);
		if (existingCompanies === undefined || existingCompanies.length === 0) {
			this.foundDuplicate = false;
			return false;
		}
		this.foundDuplicate = true;
		return true;
	};

	get name(): string {
		return this.editCompany.company?.name ?? '';
	}

	set name(name: string) {
		this.editCompany.company.name = name;
	}

	get taxId(): string {
		return this.editCompany.company?.taxId ?? '';
	}

	set taxId(taxId: string) {
		this.editCompany.company.taxId = taxId;
	}

	get active(): boolean {
		return this.editCompany.company?.active ?? false;
	}

	set active(active: boolean) {
		this.editCompany.company.active = active;
	}

	get streetName(): string {
		return this.editCompany.company?.address?.address1 ?? '';
	}

	set streetName(streetName: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.address1 = streetName;
		this.editCompany.company.address = address;
	}

	get apartmentSuite(): string {
		return this.editCompany.company?.address?.address2 ?? '';
	}

	set apartmentSuite(apartmentSuite: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.address2 = apartmentSuite;
		this.editCompany.company.address = address;
	}

	get townCity(): string {
		return this.editCompany.company?.address?.locality ?? '';
	}

	set townCity(townCity: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.locality = townCity;
		this.editCompany.company.address = address;
	}

	get stateProvince(): string {
		return this.editCompany.company?.address?.stateProvinceRegion ?? '';
	}

	set stateProvince(stateProvince: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.stateProvinceRegion = stateProvince;
		this.editCompany.company.address = address;
	}

	get zipPostalCode(): string {
		return this.editCompany.company?.address?.postalCode ?? '';
	}

	set zipPostalCode(zipPostalCode: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.postalCode = zipPostalCode;
		this.editCompany.company.address = address;
	}

	get countryCode(): string {
		return this.editCompany.company?.address?.countryCode ?? '';
	}

	set countryCode(countryCode: string) {
		const address =
			this.editCompany.company.address === undefined
				? emptyAddress()
				: this.editCompany.company.address;
		address.countryCode = countryCode;
		this.editCompany.company.address = address;
	}

	get timezone(): string {
		return this.editCompany.company?.timezone ?? '';
	}

	set timezone(timezone: string) {
		this.editCompany.company.timezone = timezone;
	}

	get companyTypeValue(): string {
		return this.editCompany.company?.companyType ?? 'advanced';
	}

	set companyTypeValue(type: string) {
		this.editCompany.company.companyType = type as 'advanced' | 'care';
	}

	onUserSelectedEvent(event: SelectedUserEvent): void {
		this.editCompany.adminUser.user = event.user;
		this.editCompany.adminUser.userType = event.userType;
	}

	filterCountries(value: any): void {
		this.filteredCountries = of(this._filter(value));
	}

	private _filter(value: string): Country[] {
		const filterValue = value.toLowerCase();
		return this.allCountries.filter((country) =>
			country.name.toLowerCase().includes(filterValue)
		);
	}

	displayCountryName = (twoLetterCode: string): string => {
		let foundCountry = null;
		foundCountry = this.allCountries.find(
			(country) => country.twoLetterCode === twoLetterCode
		);

		return foundCountry && foundCountry.name ? foundCountry.name : '';
	};

	validateCountryCode(country: string | undefined): boolean {
		if (!country) {
			return false;
		}

		if (country.length > 2) {
			return false;
		} else if (country.length < 2) {
			return false;
		} else {
			return true;
		}
	}

	displayStatusName(status: boolean): string {
		if (status) {
			return this.statusActive;
		} else {
			return this.statusInactive;
		}
	}

	getChipStyling(status: boolean): string {
		if (status) {
			return 'status-chip-active';
		} else {
			return 'status-chip-inactive';
		}
	}

	keyDetectionChanged(event: MatSlideToggleChange): void {
		// if (event.checked) {
		//   this.editCompany.company.keyDetection = '1';
		// } else {
		//   this.editCompany.company.keyDetection = '0';
		// }
	}

	onCompanyTypeChange(event: any): void {
		// if we switch to advanced company type, we will automatically disable Contact Notifications
		if (event.value === 'advanced') {
			this.editCompany.company.contactNotifications = false;
		}
	}

	isContactNotificationsEnabled(): boolean {
		// Contact Notifications can only be enabled for Care company type
		return this.companyTypeValue === 'care';
	}

	hasAlertsAndNotificationsContent(): boolean {
		return (
			this.isOracle ||
			(this.editCompany?.company?.keyDetection && !this.isOracle) ||
			this.isSentriKeyAccessUser
		);
	}

	hasDeviceManagementContent(): boolean {
		return this.isOracle;
	}

	hasSystemIntegrationsContent(): boolean {
		return this.isSentriKeyAccessUser || this.isOracle;
	}

	setupDynamicAjaxValidators(): void {
		const usernameControl = this.ajaxFormGroup.get('ajaxUsername');
		const passwordControl = this.ajaxFormGroup.get('ajaxPassword');

		passwordControl?.setValidators(() => {
			if (
				this.editCompany?.company?.ajaxEnabled &&
				!this.isInvalidString(usernameControl?.value)
			) {
				return Validators.required(passwordControl);
			}
			return null;
		});

		// Subscribe to value changes to trigger re-validation
		usernameControl?.valueChanges.subscribe(() => {
			passwordControl?.updateValueAndValidity();
		});

		// Initial validation
		usernameControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
	}

	updateAjaxValidators(): void {
		// Trigger re-validation by updating form controls
		const usernameControl = this.ajaxFormGroup.get('ajaxUsername');
		const passwordControl = this.ajaxFormGroup.get('ajaxPassword');

		usernameControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
	}

	markAjaxFieldsAsTouched(): void {
		this.ajaxFormGroup.get('ajaxUsername')?.markAsTouched();
		this.ajaxFormGroup.get('ajaxPassword')?.markAsTouched();
	}

	onAjaxUsernameChange(): void {
		// Re-validate when username changes to update password requirements
		this.updateAjaxValidators();
	}

	//* Check the tax-Id & country-code :
	async validateTaxId(): Promise<void> {
		this.taxIdValidated = false;
		this.foundTaxIdDuplicate = false;

		if (this.taxId && this.countryCode) {
			const queryParams = {
				taxId: this.taxId! ?? '',
				countryCode: this.countryCode ?? '',
			};

			const isValidTaxId =
				await this.apiService.verifyTaxIdBasedOnValue(queryParams);

			if (isValidTaxId && isValidTaxId?.taxId) {
				this.taxIdValidated = true;
				this.foundTaxIdDuplicate = true;

				this.taxIdModel.control.setErrors({
					...this.taxIdModel.control.errors,
					duplicateTaxId: true,
				});
			} else {
				this.taxIdValidated = true;
				this.foundTaxIdDuplicate = false;

				if (this.taxIdModel.control.hasError('duplicateTaxId')) {
					const errors = { ...this.taxIdModel.control.errors };
					delete errors['duplicateTaxId'];

					this.taxIdModel.control.setErrors(
						Object.keys(errors).length ? errors : null
					);
				}
			}
		}
	}
}

export enum CompanyType {
	newCompany,
	existingCompany,
}

export interface AboutCompanyEvent {
	company: Company;
	companyType: CompanyType;
	user: User;
	userType: UserType;
}

export interface CompanyRelationshipEvent {
	relationshipType: string;
	customCompanyId?: string;
	notes?: string;
}
